import { create } from 'zustand';
import { canStayConnected, hasValidToken } from '@/platform/googleAuth';
import type { AppSession } from '@/services/appSession';
import { autoDriveBackup } from '@/services/driveBackup';
import {
  connectDrive,
  disconnectDrive,
  getSyncStatus,
  isDriveConnected,
  openConflicts,
  pendingChanges,
  runSync,
  syncErrorText,
} from '@/services/syncService';

/** 토큰을 쓸 수 있는 상태: 유효한 액세스 토큰이 있거나, 갱신 토큰으로 조용히 받을 수 있음 */
export const authUsable = () => hasValidToken() || canStayConnected();

type SyncUi = {
  connected: boolean;
  tokenValid: boolean;
  stayConnected: boolean;
  running: boolean;
  lastSyncAt: string | null;
  error: string | null;
  notice: string | null;
  errorName: string | null;
  conflicts: number;
  pending: number;
  refresh: (s: AppSession) => Promise<void>;
  connect: (s: AppSession) => Promise<void>;
  disconnect: (s: AppSession) => Promise<void>;
  syncNow: (s: AppSession) => Promise<void>;
};

export const useSyncUi = create<SyncUi>((set, get) => ({
  connected: false,
  tokenValid: false,
  stayConnected: false,
  running: false,
  lastSyncAt: null,
  error: null,
  notice: null,
  errorName: null,
  conflicts: 0,
  pending: 0,

  async refresh(s) {
    const [connected, status, conflicts, pending] = await Promise.all([
      isDriveConnected(s),
      getSyncStatus(s),
      openConflicts(s),
      pendingChanges(s),
    ]);
    set({
      connected,
      tokenValid: authUsable(),
      stayConnected: canStayConnected(),
      lastSyncAt: status.lastSyncAt,
      conflicts: conflicts.length,
      pending,
      ...(status.lastError ? { errorName: status.lastError } : { errorName: null, error: null }),
    });
  },

  /** 반드시 버튼 클릭 안에서 호출 (Google 팝업) */
  async connect(s) {
    set({ error: null, notice: null });
    try {
      const stay = await connectDrive(s);
      set({
        connected: true,
        tokenValid: true,
        stayConnected: stay,
        notice: stay ? null : '연결되었습니다. 아직 "계속 연결"이 설정되지 않아 약 1시간 뒤 다시 연결이 필요합니다.',
      });
      await get().syncNow(s);
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  async disconnect(s) {
    await disconnectDrive(s);
    await get().refresh(s);
  },

  async syncNow(s) {
    if (get().running) return;
    if (!authUsable()) {
      set({ tokenValid: false });
      return;
    }
    set({ running: true, error: null });
    try {
      await runSync(s);
      set({ error: null, errorName: null });
      // 동기화 뒤 마지막 Drive 백업이 7일 지났으면 자동으로 백업 (실패해도 동기화 결과에는 영향 없음)
      void autoDriveBackup(s).catch(() => undefined);
    } catch (e) {
      set({ error: syncErrorText(e), errorName: (e as Error)?.name ?? null });
    } finally {
      set({ running: false });
      await get().refresh(s);
    }
  },
}));
